import {
    BankingGeneralDisclosure,
    CashApyDisclosure,
    CashBackDisclosure,
} from '@fruitful/compliance';
import { LinearGradient } from 'expo-linear-gradient';
import { Link } from 'expo-router';
import { type ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import {
    BodyLargeRegular,
    BodySmallEmphasized,
    FontFamilyName,
    H6Emphasized,
    Icon,
    Info,
    LabelRegular,
    Text,
    TooltipTrigger,
    XStack,
    YStack,
} from 'smoothie';

import LeavesImage from '../../assets/images/leaves.svg';
import StarsImage from '../../assets/images/stars.svg';

export const LinearGradientBackgroundCard = ({
    children,
}: {
    children: ReactNode;
}) => {
    return (
        <View style={cardStyles.container}>
            <LinearGradient
                colors={['#DAEDE4', '#B4D0D8']}
                locations={[0, 1]}
                start={{ x: 0.41, y: 0 }}
                end={{ x: 0.59, y: 1 }}
                style={StyleSheet.absoluteFill}
            />
            {children}
        </View>
    );
};

const cardStyles = StyleSheet.create({
    container: {
        width: '100%',
        borderRadius: 24,
        overflow: 'hidden',
        paddingVertical: 16,
        paddingHorizontal: 8,
        position: 'relative',
    },
});

export const Header = ({
    title,
    description,
}: {
    title: string;
    description?: string;
}) => {
    return (
        <YStack gap={8} width="100%">
            <YStack gap={4} width="100%">
                <LabelRegular textTransform="uppercase">
                    your money system
                </LabelRegular>
                <Text
                    fontSize={28}
                    fontWeight={600}
                    lineHeight={36}
                    letterSpacing="-0.56px"
                    style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
                >
                    {title}
                </Text>
            </YStack>
            {description != null && (
                <BodyLargeRegular
                    style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
                >
                    {description}
                </BodyLargeRegular>
            )}
        </YStack>
    );
};

export const BankingDisclosures = ({
    color = '$gray1000',
    includeCard = true,
}: {
    color?: string;
    includeCard?: boolean;
}) => {
    return (
        <YStack
            width="100%"
            justifyContent="center"
            alignItems="center"
            gap={12}
        >
            <BankingGeneralDisclosure color={color} />
            <YStack width="100%">
                <CashApyDisclosure
                    color={color}
                    titleColor={color}
                    superscript={1}
                />
            </YStack>
            {includeCard && (
                <YStack width="100%">
                    <CashBackDisclosure
                        color={color}
                        titleColor={color}
                        superscript={2}
                    />
                </YStack>
            )}
        </YStack>
    );
};

export const AdvisoryDisclosure = () => (
    <LabelRegular color="$gray1000">
        Investment advice offered by Fruitful Advisory, LLC, a registered
        investment adviser. Examples shown for illustrative purposes only.
        Please visit{' '}
        <Link href="https://www.fruitful.com/legal" target="_blank">
            <LabelRegular textDecorationLine="underline" color="$gray1000">
                fruitful.com/legal
            </LabelRegular>
        </Link>{' '}
        for additional disclosures.
    </LabelRegular>
);

export const InvestingDisclosures = ({
    includeTaxDisclosure = false,
}: {
    includeTaxDisclosure?: boolean;
}) => {
    return (
        <YStack width="100%" gap={12}>
            <AdvisoryDisclosure />
            <LabelRegular color="$gray1000">
                Investing involves risk, including the possible loss of
                principal. It isn&apos;t possible to invest directly in an
                index. Past performance does not guarantee future results.
            </LabelRegular>
            {includeTaxDisclosure && (
                <LabelRegular color="$gray1000">
                    This material is provided for informational purposes only
                    and should not be construed as tax or legal advice.
                    Investors should consult their own tax advisor regarding
                    their individual circumstances.
                </LabelRegular>
            )}
            <LabelRegular color="$gray1000">
                Investments are held at and cleared by APEX Clearing
                Corporation, member FINRA/SIPC.
            </LabelRegular>
        </YStack>
    );
};

export const TrustpilotRatingBadge = () => (
    <XStack
        alignSelf="center"
        alignItems="center"
        justifyContent="center"
        gap={16}
    >
        <LeavesImage width={28} height={69} />
        <YStack flex={1} justifyContent="center" alignItems="center" gap={8}>
            <YStack justifyContent="center" alignItems="center">
                <H6Emphasized>Excellent</H6Emphasized>
                <LabelRegular>Rated 4.8/5</LabelRegular>
            </YStack>
            <StarsImage width={110} height={18} />
            <XStack alignItems="center" gap={4}>
                <BodySmallEmphasized>Trustpilot</BodySmallEmphasized>
                <TooltipTrigger tooltipContent="Trustpilot rating based on reviews as of September 1st, 2026. Reviews may not reflect all members experience and are not indicative of future performance. All reviews on Trustpilot can be found at Trustpilot.com/review/fruitful.com.">
                    <XStack cursor="pointer">
                        <Icon icon={Info} size={14} />
                    </XStack>
                </TooltipTrigger>
            </XStack>
        </YStack>
        <LeavesImage
            width={28}
            height={69}
            style={{ transform: [{ scaleX: -1 }] }}
        />
    </XStack>
);

export const Section = ({ title, body }: { title?: string; body: string }) => (
    <YStack gap={4} width="100%">
        {title != null && (
            <Text
                fontSize={18}
                style={{ fontFamily: FontFamilyName.SourceSerifSemiBold }}
            >
                {title}
            </Text>
        )}
        <Text
            fontSize={18}
            style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
        >
            {body}
        </Text>
    </YStack>
);
